import { MapNumberTo, MapStringTo, NullableResultPromise } from "../base-types";
import { DEFINITIONS } from "../constants";
import { IEntity } from "../contracts/base/entity";
import { CultureLoadOption } from "../contracts/querying/culture-load-option";
import { EntityLoadConfiguration } from "../contracts/querying/entity-load-configuration";
import {
    ComparisonOperator,
    CompositeFilterOperator,
    CompositeQueryFilter,
    DefinitionQueryFilter,
    FilterDataType,
    PropertyQueryFilter,
} from "../contracts/querying/filters";
import { PropertyLoadOption } from "../contracts/querying/property-load-option";
import { Query } from "../contracts/querying/query";
import { RelationLoadOption } from "../contracts/querying/relation-load-option";
import Guard from "../guard";
import SetPasswordRequestResource from "../models/set-password-request-resource";
import { TypeGuards } from "../type-guards";
import { IExtendedContentHubClient } from "./extended-client";
import { ResponseHandler } from "./response-handler";

export interface IUsersClient {
    //#region Get users

    /**
     * Gets the id of the specified user by username.
     *
     * @param username - The user name
     */
    getUserIdAsync(username: string): NullableResultPromise<number>;

    /**
     * Gets the ids of the specified users by username.
     *
     * @param usernames - A list of user names
     * @returns Array of IDs of type MapStringTo<number>.
     */
    getUsernamesToIdsMapAsync(usernames: Array<string>): Promise<MapStringTo<number>>;

    /**
     * Gets the ids of the specified users by username.
     * @param usernames - A list of user names
     * @returns Array of IDs of type Array<number> or null.
     */
    getUserIdsAsync(usernames: Array<string>): NullableResultPromise<Array<number>>;

    /**
     * Gets the username of the specified user.
     *
     * @param id - The id of the user
     */
    getUsernameAsync(id: number): NullableResultPromise<string>;

    /**
     * Gets the usernames of the specified users.
     *
     * @param ids - The ids of the entities to get
     */
    getUsernamesAsync(ids: Array<number>): Promise<MapNumberTo<string>>;

    /**
     * Gets the specified user entity.
     *
     * @param id - The id of the user
     * @param loadConfiguration - The load configuration
     */
    getUserAsync(id: number, loadConfiguration: EntityLoadConfiguration): NullableResultPromise<IEntity>;

    /**
     * Gets the specified user entities.
     *
     * @param username - Username of the user
     * @param loadConfiguration - The load configuration
     */
    getUserAsync(username: string, loadConfiguration: EntityLoadConfiguration): NullableResultPromise<IEntity>;

    /**
     * Gets the specified user entities.
     *
     * @param ids - The ids of the users
     * @param loadConfiguration - The load configuration
     */
    getUsersAsync(ids: Array<number>, loadConfiguration: EntityLoadConfiguration): Promise<Array<IEntity>>;

    /**
     * Gets the specified user entities.
     *
     * @param usernames - A list of user names
     * @param loadConfiguration - The load configuration
     */
    getUsersAsync(
        usernames: Array<string>,
        loadConfiguration: EntityLoadConfiguration
    ): NullableResultPromise<Array<IEntity>>;

    //#endregion

    //#region Get user groups

    /**
     * Gets the id of the specified user group by name.
     *
     * @param groupName - The groupName name
     */
    getUserGroupIdAsync(groupName: string): NullableResultPromise<number>;

    /**
     * Gets the ids of the specified user groups by name.
     *
     * @param groupNames - A list of group names
     */
    getUserGroupIdsAsync(groupNames: Array<string>): Promise<MapStringTo<number>>;

    /**
     * Gets the name of the specified user group
     *
     * @param number - The id of the user group
     */
    getUserGroupNameAsync(id: number): NullableResultPromise<string>;

    /**
     * Gets the name of the specified user groups.
     *
     * @param ids - The ids of user groups
     */
    getUserGroupNamesAsync(ids: Array<number>): Promise<MapNumberTo<string>>;

    /**
     * Gets the specified user group entity.
     *
     * @param id - Id of the user
     * @param loadConfiguration - The load configuration
     */
    getUserGroupAsync(id: number, loadConfiguration: EntityLoadConfiguration): NullableResultPromise<IEntity>;

    /**
     * Gets the specified user group entity.
     *
     * @param groupName - Name of the group
     * @param loadConfiguration - The load configuration
     */
    getUserGroupAsync(groupName: string, loadConfiguration: EntityLoadConfiguration): NullableResultPromise<IEntity>;

    /**
     * Gets the specified user group entities.
     *
     * @param ids - The ids of the user groups
     * @param loadConfiguration - The load configuration
     */
    getUserGroupsAsync(
        ids: Array<number>,
        loadConfiguration: EntityLoadConfiguration
    ): NullableResultPromise<Array<IEntity>>;

    /**
     * Gets the specified user group entities.
     *
     * @param groupNames - The names of the user groups
     * @param loadConfiguration - The load configuration
     */
    getUserGroupsAsync(
        groupNames: Array<string>,
        loadConfiguration: EntityLoadConfiguration
    ): NullableResultPromise<Array<IEntity>>;

    //#endregion

    //#region Password management

    /**
     * Changes the password of the specified user.
     *
     * @param id - The user id to change the password on
     * @param password - The password to reset
     */
    setPasswordAsync(id: number, password: string): Promise<void>;

    /**
     * Resets the password of the specified user.
     * This sends an e-mail to the specified user with a link to choose a new password.
     *
     * @param id - Id of the user
     */
    resetPasswordAsync(id: number): Promise<void>;

    //#endregion
}

export class UsersClient implements IUsersClient {
    protected readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        this._client = client;
    }

    //#region Get users

    async getUserIdAsync(username: string): NullableResultPromise<number> {
        Guard.stringNotNullOrEmpty(username);

        const user = await this.getUserAsync(username, EntityLoadConfiguration.Minimal);

        return user != null ? user.id! : null;
    }

    async getUserIdsAsync(usernames: Array<string>): NullableResultPromise<Array<number>> {
        if (usernames == null) return null;

        if (usernames.length === 0) return [];

        Guard.arrayNoneNullOrEmptyString(usernames);

        const theQuery = new Query({
            filter: new CompositeQueryFilter({
                combineMethod: CompositeFilterOperator.And,
                children: [
                    new DefinitionQueryFilter({
                        name: DEFINITIONS["User"].definitionName,
                        operator: ComparisonOperator.Equals,
                    }),
                    new PropertyQueryFilter({
                        operator: ComparisonOperator.Equals,
                        dataType: FilterDataType.String,
                        property: DEFINITIONS["User"].username,
                        values: usernames,
                    }),
                ],
            }),
        });

        const result = await this._client.querying.queryIdsAsync(theQuery);

        return result.items;
    }

    async getUsernamesToIdsMapAsync(usernames: Array<string>): Promise<MapStringTo<number>> {
        Guard.arrayNoneNullOrEmptyString(usernames);

        const loadConfiguration = new EntityLoadConfiguration(
            CultureLoadOption.None,
            new PropertyLoadOption(DEFINITIONS["User"].username)
        );
        const users = await this.getUsersAsync(usernames, loadConfiguration);
        const userObject: MapStringTo<number> = {};

        if (users.length) {
            users.forEach((user: IEntity) => {
                const username = user.getPropertyValue<"String">(DEFINITIONS["User"].username)!;
                userObject[username] = user.id!;
            });
        }

        return userObject;
    }

    async getUsernameAsync(id: number): NullableResultPromise<string> {
        Guard.validId(id);

        const loadConfiguration = new EntityLoadConfiguration(
            CultureLoadOption.None,
            new PropertyLoadOption(DEFINITIONS["User"].username),
            RelationLoadOption.None
        );

        const user = await this.getUserAsync(id, loadConfiguration);

        if (user) {
            const username = user.getPropertyValue<"String">(DEFINITIONS["User"].username);

            return username;
        } else {
            return null;
        }
    }

    async getUsernamesAsync(ids: Array<number>): Promise<MapNumberTo<string>> {
        Guard.validIds(ids);

        const loadConfiguration = new EntityLoadConfiguration(
            CultureLoadOption.None,
            new PropertyLoadOption(DEFINITIONS["User"].username),
            RelationLoadOption.None
        );

        const users = await this.getUsersAsync(ids, loadConfiguration);

        const usersObject: MapNumberTo<string> = {};
        if (users && users.length) {
            users.forEach((user: IEntity) => {
                const username = user.getPropertyValue<"String">(DEFINITIONS["User"].username)!;
                usersObject[user.id!] = username;
            });
        }
        return usersObject;
    }

    async getUserAsync(
        param: string | number,
        loadConfiguration: EntityLoadConfiguration
    ): NullableResultPromise<IEntity> {
        Guard.notNullOrUndefined(param);

        if (typeof param === "string") {
            Guard.stringNotNullOrEmpty(param);

            const theQuery = new Query({
                filter: new CompositeQueryFilter({
                    combineMethod: CompositeFilterOperator.And,
                    children: [
                        new DefinitionQueryFilter({
                            name: DEFINITIONS["User"].definitionName,
                            operator: ComparisonOperator.Equals,
                        }),
                        new PropertyQueryFilter({
                            operator: ComparisonOperator.Equals,
                            dataType: FilterDataType.String,
                            property: DEFINITIONS["User"].username,
                            value: param,
                        }),
                    ],
                }),
                take: 1,
            });

            const entity = await this._client.querying.singleAsync(theQuery, loadConfiguration);

            return entity;
        } else {
            const user = await this._client.entities.getAsync(param, loadConfiguration);

            return user;
        }
    }

    async getUsersAsync(
        param: Array<number> | Array<string>,
        loadConfiguration: EntityLoadConfiguration
    ): Promise<Array<IEntity>> {
        Guard.notNullOrUndefined(param);

        if (param.length === 0) {
            return [];
        } else if (TypeGuards.isNumberArray(param)) {
            Guard.validIds(param);

            const users = await this._client.entities.getManyAsync(param, loadConfiguration);

            return users;
        } else {
            Guard.arrayNoneNullOrEmptyString(param);

            const theQuery = new Query({
                filter: new CompositeQueryFilter({
                    combineMethod: CompositeFilterOperator.And,
                    children: [
                        new DefinitionQueryFilter({
                            name: DEFINITIONS["User"].definitionName,
                            operator: ComparisonOperator.Equals,
                        }),
                        new PropertyQueryFilter({
                            operator: ComparisonOperator.Equals,
                            dataType: FilterDataType.String,
                            property: DEFINITIONS["User"].username,
                            values: param,
                        }),
                    ],
                }),
            });

            const entities = await this._client.querying.queryAsync(theQuery, loadConfiguration);

            return entities.items;
        }
    }

    //#endregion

    //#region Get user groups

    async getUserGroupIdAsync(groupName: string): NullableResultPromise<number> {
        Guard.stringNotNullOrEmpty(groupName);

        const group = await this.getUserGroupAsync(groupName, EntityLoadConfiguration.Minimal);

        return group != null ? group.id! : null;
    }

    async getUserGroupIdsAsync(groupNames: Array<string>): Promise<MapStringTo<number>> {
        Guard.arrayNoneNullOrEmptyString(groupNames);

        const loadConfig = new EntityLoadConfiguration(
            CultureLoadOption.None,
            new PropertyLoadOption(DEFINITIONS["UserGroup"].groupName),
            RelationLoadOption.None
        );

        const userGroups = await this.getUserGroupsAsync(groupNames, loadConfig);
        const userGroupObject: MapStringTo<number> = {};

        if (userGroups && userGroups.length) {
            userGroups.forEach((userGroup: IEntity) => {
                const groupName = userGroup.getPropertyValue<"String">(DEFINITIONS["UserGroup"].groupName)!;
                userGroupObject[groupName] = userGroup.id!;
            });
        }

        return userGroupObject;
    }

    async getUserGroupNameAsync(id: number): NullableResultPromise<string> {
        Guard.validId(id);

        const loadConfig = new EntityLoadConfiguration(
            CultureLoadOption.None,
            new PropertyLoadOption(DEFINITIONS["UserGroup"].groupName),
            RelationLoadOption.None
        );

        const userGroup = await this.getUserGroupAsync(id, loadConfig);

        if (!userGroup) {
            return null;
        }

        const groupName = userGroup.getPropertyValue<"String">(DEFINITIONS["UserGroup"].groupName)!;

        return groupName;
    }

    async getUserGroupNamesAsync(ids: Array<number>): Promise<MapNumberTo<string>> {
        Guard.validIds(ids);

        const loadConfig = new EntityLoadConfiguration(
            CultureLoadOption.None,
            new PropertyLoadOption(DEFINITIONS["UserGroup"].groupName),
            RelationLoadOption.None
        );

        const userGroups = await this.getUserGroupsAsync(ids, loadConfig);

        const userGroupObject: MapNumberTo<string> = {};
        if (userGroups && userGroups.length) {
            userGroups.forEach((userGroup: IEntity) => {
                const groupName = userGroup.getPropertyValue<"String">(DEFINITIONS["UserGroup"].groupName)!;
                userGroupObject[userGroup.id!] = groupName;
            });
        }
        return userGroupObject;
    }

    async getUserGroupAsync(
        param: string | number,
        loadConfiguration: EntityLoadConfiguration
    ): NullableResultPromise<IEntity> {
        Guard.notNullOrUndefined(param);

        if (typeof param === "string") {
            Guard.stringNotNullOrEmpty(param);

            const theQuery = new Query({
                filter: new CompositeQueryFilter({
                    combineMethod: CompositeFilterOperator.And,
                    children: [
                        new DefinitionQueryFilter({
                            name: DEFINITIONS["UserGroup"].definitionName,
                            operator: ComparisonOperator.Equals,
                        }),
                        new PropertyQueryFilter({
                            operator: ComparisonOperator.Equals,
                            dataType: FilterDataType.String,
                            property: DEFINITIONS["UserGroup"].groupName,
                            value: param,
                        }),
                    ],
                }),
                take: 1,
            });

            const entity = await this._client.querying.singleAsync(theQuery, loadConfiguration);

            return entity;
        } else {
            Guard.validId(param);

            const userGroup = this._client.entities.getAsync(param, loadConfiguration);

            return userGroup;
        }
    }

    async getUserGroupsAsync(
        param: Array<number> | Array<string>,
        loadConfiguration: EntityLoadConfiguration
    ): NullableResultPromise<Array<IEntity>> {
        Guard.notNullOrUndefined(param);

        if (param.length === 0) {
            return [];
        } else if (TypeGuards.isNumberArray(param)) {
            Guard.validIds(param);

            const userGroups = await this._client.entities.getManyAsync(param, loadConfiguration);
            return userGroups;
        } else {
            Guard.arrayNoneNullOrEmptyString(param);

            const theQuery = new Query({
                filter: new CompositeQueryFilter({
                    combineMethod: CompositeFilterOperator.And,
                    children: [
                        new DefinitionQueryFilter({
                            name: DEFINITIONS["UserGroup"].definitionName,
                            operator: ComparisonOperator.Equals,
                        }),
                        new PropertyQueryFilter({
                            operator: ComparisonOperator.Equals,
                            dataType: FilterDataType.String,
                            property: DEFINITIONS["UserGroup"].groupName,
                            values: param,
                        }),
                    ],
                }),
            });

            const entities = await this._client.querying.queryAsync(theQuery, loadConfiguration);

            return entities.items;
        }
    }

    //#endregion

    //#region Password management

    async setPasswordAsync(id: number, password: string): Promise<void> {
        Guard.validId(id);

        const link = await this._client.linkHelper.setUserPasswordToLinkAsync(id);
        const content = new SetPasswordRequestResource(password);
        const response = await this._client.raw.postAsync(link.href, content);

        ResponseHandler.handleErrors(response);
    }

    async resetPasswordAsync(id: number): Promise<void> {
        Guard.validId(id);

        const link = await this._client.linkHelper.resetPasswordToLinkAsync(id);
        const response = await this._client.raw.postAsync(link.href);

        ResponseHandler.handleErrors(response);
    }

    //#endregion
}
